import { useQuery, useQueryClient } from '@tanstack/react-query';
import { platformApi } from '../../../../api/endpoints/platform';
import { RoleFormCore } from '../../../../components/roles/RoleFormCore';
import type { TenantRoleListItemLocal } from '../../../../api/types/TenantLocal';

interface Props {
    /** tenantId del DRILL-DOWN (param de ruta). El rol vive en ESTE tenant, no en el del actor. */
    tenantId: string;
    /** Si viene, el form edita ese rol (roleId fijo); si no, crea uno nuevo. */
    role?: TenantRoleListItemLocal;
    /** Se llama tras crear/editar OK (ej. cerrar el modal del padre). */
    onDone?: () => void;
}

/**
 * Alta/edición de un rol custom en el módulo Platform (drill-down de un tenant). Wrapper delgado sobre
 * `RoleFormCore`: el `tenantId` viene del PATH (no del user logueado) y consume `platformApi`. El
 * catálogo de permisos del picker es el de la APP del tenant (no el global, que quedó pelado tras la
 * purga de permisos de dominio y nunca trae los CUSTOM de cada app); el `applicationId` sale del detalle
 * del tenant (mismo queryKey que `TenantDetail`, se reusa la cache). Sin app → el picker cae al global.
 */
export function PlatformTenantRoleForm({ tenantId, role, onDone }: Props) {
    const queryClient = useQueryClient();
    const { data: tenant } = useQuery({
        queryKey: ['platform', 'tenants', tenantId],
        queryFn: () => platformApi.getTenant(tenantId),
        staleTime: 5 * 60_000,
    });
    const applicationId = tenant?.applicationId;

    return (
        <RoleFormCore
            strategy={{
                supportsEdit: true,
                role: role ? { roleId: role.roleId } : undefined,
                // El nombre editable arranca del `roleName` del rol (comportamiento preservado del flujo).
                initial: {
                    displayName: role?.roleName ?? '',
                    description: role?.description ?? '',
                    permissions: role?.permissions ?? [],
                },
                create: (body, opts) => platformApi.createTenantRole(tenantId, body, opts),
                update: role
                    ? (body) => platformApi.updateTenantRole(tenantId, role.roleId, body)
                    : undefined,
                invalidate: () =>
                    queryClient.invalidateQueries({
                        queryKey: ['platform', 'tenants', tenantId, 'roles'],
                    }),
                permissionPickerProps: { applicationId },
                onDone,
                idPrefix: 'ptrf',
                nameHint: 'Como quieres que se vea. Ej: Supervisor de Cobranza',
                permissionsLabel: 'Permisos',
                createSubmitLabel: 'Crear rol',
            }}
        />
    );
}
